import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { fetchClippingProjects } from "../api/clippingProjectsApi";
import ClipProjectGroup from "./ClipProjectGroup";

export default function ClipLibraryView({ lang }) {
  const { hitAxios } = React.useContext(GlobalContext);
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchClippingProjects(hitAxios);
      setProjects(data?.projects || []);
    } catch (err) {
      setError(err?.message || "Could not load clips.");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const onJob = (event) => {
      const job = event?.detail?.job;
      if (job?.type === "clipping" && job?.phase === "complete") load();
    };
    window.addEventListener("beyundai:background-job", onJob);
    return () => window.removeEventListener("beyundai:background-job", onJob);
  }, [load]);

  if (loading) {
    return (
      <Stack alignItems="center" py={6} spacing={2}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          {lang?.aiClippingLibraryLoading || "Loading your clipped videos…"}
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            {lang?.retry || "Retry"}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const completed = projects.filter((p) => p.status === "completed" && (p.clips || []).length);

  if (!completed.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {lang?.aiClippingLibraryEmpty || "No clips yet"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto" }}>
          {lang?.aiClippingLibraryEmptyHint ||
            "Create your first clipping job to turn a long video into short-form clips. They will appear here grouped by source video."}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {(lang?.aiClippingLibraryCount || "{count} projects").replace(
            "{count}",
            String(completed.length),
          )}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshOutlined />}
          onClick={load}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {lang?.refresh || "Refresh"}
        </Button>
      </Stack>

      {completed.map((project) => (
        <ClipProjectGroup key={project.id} project={project} lang={lang} />
      ))}
    </Stack>
  );
}
